import updateDashboardGadget from '../../../api/updateDashboardGadget';
import { useCallback, useContext } from 'react';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import getIsFilterEqual from './getIsFilterEqual';
import aguid from 'lib/aguid';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';
import STORE from '../../../store';

const useOnReplaceAllClicked = ({
  matches,
  variableFilter,
}: {
  matches: {
    gadgets: DashboardGadget[];
    savedFilters: LegacySavedFilter[];
  };
  variableFilter: VariableDrillDownType;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);

  const onReplaceAllClicked = useCallback(() => {
    const newPlate = {
      id: aguid(),
      type: 'Variable',
      variableId: variableFilter.id,
    } as FilterPlateType;
    const gadgetRequests = matches.gadgets.map((gadget) => {
      const scopeWithoutReplacement = gadget.scope.filter((scopePlate) => {
        if (
          filterPlateTypeCheckers.isVariable(scopePlate) ||
          filterPlateTypeCheckers.isSavedFilter(scopePlate) ||
          filterPlateTypeCheckers.isExcludeTermedDrivers(scopePlate)
        ) {
          return true;
        } else {
          return (
            !!scopePlate.fixedValue &&
            !getIsFilterEqual(scopePlate.fixedValue, variableFilter.value)
          );
        }
      });
      const drillDownsWithoutReplacement = gadget.drillDowns.filter(
        (drillPlate) => {
          if (
            filterPlateTypeCheckers.isVariable(drillPlate) ||
            filterPlateTypeCheckers.isSavedFilter(drillPlate) ||
            filterPlateTypeCheckers.isExcludeTermedDrivers(drillPlate)
          ) {
            return true;
          } else {
            return (
              !!drillPlate.fixedValue &&
              !getIsFilterEqual(drillPlate.fixedValue, variableFilter.value)
            );
          }
        },
      );
      const newDrillDowns = [...drillDownsWithoutReplacement, newPlate];
      const newGadget = {
        ...gadget,
        scope: scopeWithoutReplacement,
        drillDowns: newDrillDowns,
      };

      return updateDashboardGadget(gadget.id, newGadget, selectedAccountId);
    });

    const savedFilterRequests = matches.savedFilters.map((savedFilter) => {
      const scopeWithoutReplacement = savedFilter.scope.filter((scopePlate) => {
        if (
          filterPlateTypeCheckers.isVariable(scopePlate) ||
          filterPlateTypeCheckers.isSavedFilter(scopePlate) ||
          filterPlateTypeCheckers.isExcludeTermedDrivers(scopePlate)
        ) {
          return true;
        } else {
          return (
            !!scopePlate.fixedValue &&
            !getIsFilterEqual(scopePlate.fixedValue, variableFilter.value)
          );
        }
      });
      const drillDownsWithoutReplacement = savedFilter.drillDowns.filter(
        (drillPlate) => {
          if (
            filterPlateTypeCheckers.isVariable(drillPlate) ||
            filterPlateTypeCheckers.isSavedFilter(drillPlate) ||
            filterPlateTypeCheckers.isExcludeTermedDrivers(drillPlate)
          ) {
            return true;
          } else {
            return (
              !!drillPlate.fixedValue &&
              !getIsFilterEqual(drillPlate.fixedValue, variableFilter.value)
            );
          }
        },
      );
      const newDrillDowns = [...drillDownsWithoutReplacement, newPlate];
      const newSavedFilter = {
        ...savedFilter,
        scope: scopeWithoutReplacement,
        drillDowns: newDrillDowns,
      };

      return STORE.filterDefinitions
        .getLegacySavedFiltersRef({ accountId: selectedAccountId })
        .doc(newSavedFilter.id)
        .set(newSavedFilter);
    });
    Promise.all([...gadgetRequests, ...savedFilterRequests]).then(() => {});
  }, [
    matches.gadgets,
    matches.savedFilters,
    selectedAccountId,
    variableFilter.id,
    variableFilter.value,
  ]);

  return onReplaceAllClicked;
};

export default useOnReplaceAllClicked;
